import React, { useMemo, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserContext } from "../../context/UserContext";
import WorkshopsPage from "../workshops/WorkshopsPage";
import WorkshopDetail from "../workshops/WorkshopDetail";
import WorkshopCreationForm from "../workshops/WorkshopCreationForm";
import MyWorkshops from "../workshops/MyWorkshops";
import PaymentProofUpload from "../workshops/PaymentProofUpload";
import EditRequest from "../workshops/EditRequest";
import ParticipantManagement from "../workshops/ParticipantManagement";
import { workshopApi } from "../../services/workshopApi";
import "./PersistentWorkshopsLayout.css";

// ============================================
// SCROLL CACHE - For External Navigation (e.g., to /profile)
// ============================================
const SCROLL_CACHE_KEY = 'workshops_scroll_cache';

/**
 * PersistentWorkshopsLayout - Twitter/Reddit Style Navigation
 * 
 * This layout ensures the workshops list NEVER unmounts when navigating to workshop details.
 * 
 * Architecture:
 * - WorkshopsPage is ALWAYS rendered at full size
 * - When viewing workshop detail/forms, list is hidden via visibility:hidden
 * - Detail pages render on TOP
 * - Scroll position is preserved because list dimensions don't change
 * 
 * EXTERNAL NAVIGATION (to /profile, etc.):
 * - When this layout unmounts, scroll position is saved to sessionStorage
 * - When remounting, scroll position is restored from cache
 * 
 * Routes covered:
 * - /workshops              → List visible (Discover)
 * - /workshops/my-workshops → MyWorkshops visible, main list hidden
 * - /workshops/create       → Create form visible, list hidden
 * - /workshops/:id          → Detail visible, list hidden
 * - /workshops/:id/edit     → Edit form visible, list hidden
 * - /workshops/:id/payment  → Payment page visible, list hidden
 * - /workshops/:id/edit-request → Edit request visible, list hidden
 * - /workshops/:id/participants → Participants visible, list hidden
 * 
 * KEY INSIGHT: We maintain FIXED ORDER of container divs to prevent
 * React sibling reconciliation from remounting components.
 */
const PersistentWorkshopsLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUserContext();
  const listContainerRef = useRef(null);
  const hasRestoredScrollRef = useRef(false);
  
  // ========================================
  // SCROLL CACHE FOR EXTERNAL NAVIGATION
  // ========================================
  
  // Save scroll position when unmounting (navigating to /profile, etc.)
  useEffect(() => {
    const container = listContainerRef.current;
    
    return () => {
      if (container) {
        const scrollTop = container.scrollTop;
        try {
          sessionStorage.setItem(SCROLL_CACHE_KEY, JSON.stringify({
            scrollTop,
            timestamp: Date.now(),
            path: location.pathname,
          }));
} catch (e) {
          // Ignore storage errors
        }
      }
    };
  }, [location.pathname]);
  
  // Restore scroll position when mounting (coming back from /profile, etc.)
  useEffect(() => {
    if (hasRestoredScrollRef.current) return;
    
    const container = listContainerRef.current;
    if (!container) return;
    
    try {
      const cached = sessionStorage.getItem(SCROLL_CACHE_KEY);
      if (!cached) return;
      
      const { scrollTop, timestamp } = JSON.parse(cached);
      
      // Only restore if cache is less than 30 minutes old
      const maxAge = 30 * 60 * 1000;
      if (Date.now() - timestamp > maxAge) {
        sessionStorage.removeItem(SCROLL_CACHE_KEY);
        return;
      }
      
      // Restore scroll with retry mechanism
      // This handles cases where React Query is still hydrating data
      let attempts = 0;
      const maxAttempts = 10;
      
      const attemptRestore = () => {
        if (hasRestoredScrollRef.current || attempts >= maxAttempts) return;
        
        attempts++;
        const currentContainer = listContainerRef.current;
        
        if (currentContainer) {
          // Check if content is ready (scrollHeight > viewport height)
          if (currentContainer.scrollHeight > currentContainer.clientHeight || scrollTop === 0) {
            currentContainer.scrollTop = scrollTop;
            hasRestoredScrollRef.current = true;
} else {
            // Content not ready yet, retry
            setTimeout(attemptRestore, 100);
          }
        }
      };
      
      // Start restoration after initial render
      requestAnimationFrame(() => {
        setTimeout(attemptRestore, 50);
      });
    } catch (e) {
      // Ignore
    }
  }, []);
  
  // Parse URL to determine what to show
  const urlInfo = useMemo(() => {
    const path = location.pathname;
    
    // /workshops - main list
    if (path === "/workshops" || path === "/workshops/") {
      return { view: "list", workshopId: null, subView: null };
    }
    
    // /workshops/my-workshops
    if (path === "/workshops/my-workshops" || path.startsWith("/workshops/my-workshops")) {
      return { view: "my-workshops", workshopId: null, subView: null };
    }
    
    // /workshops/create
    if (path === "/workshops/create") {
      return { view: "create", workshopId: null, subView: null };
    }
    
    // /workshops/legacy (for backward compat)
    if (path === "/workshops/legacy") {
      return { view: "list", workshopId: null, subView: null };
    }
    
    // /workshops/my-applications - redirect handled by component
    if (path === "/workshops/my-applications") {
      return { view: "my-workshops", workshopId: null, subView: "requests" };
    }
    
    // /workshops/:workshopId/edit
    const editMatch = path.match(/^\/workshops\/([a-f0-9]{24})\/edit$/i);
    if (editMatch) {
      return { view: "edit", workshopId: editMatch[1], subView: null };
    }
    
    // /workshops/:workshopId/payment
    const paymentMatch = path.match(/^\/workshops\/([a-f0-9]{24})\/payment$/i);
    if (paymentMatch) {
      return { view: "payment", workshopId: paymentMatch[1], subView: null };
    }
    
    // /workshops/:workshopId/edit-request
    const editRequestMatch = path.match(/^\/workshops\/([a-f0-9]{24})\/edit-request$/i);
    if (editRequestMatch) {
      return { view: "edit-request", workshopId: editRequestMatch[1], subView: null };
    }
    
    // /workshops/:workshopId/participants
    const participantsMatch = path.match(/^\/workshops\/([a-f0-9]{24})\/participants$/i);
    if (participantsMatch) {
      return { view: "participants", workshopId: participantsMatch[1], subView: null };
    }
    
    // /workshops/:workshopId - detail view (catch-all for workshop IDs)
    const detailMatch = path.match(/^\/workshops\/([a-f0-9]{24})$/i);
    if (detailMatch) {
      return { view: "detail", workshopId: detailMatch[1], subView: null };
    }
    
    // Fallback - treat as list
    return { view: "list", workshopId: null, subView: null };
  }, [location.pathname]);

  const { view, workshopId, subView } = urlInfo;
  
  // Determine visibility states
  const isListVisible = view === "list";
  const isMyWorkshopsVisible = view === "my-workshops";
  const isDetailVisible = view === "detail";
  const isCreateVisible = view === "create";
  const isEditVisible = view === "edit";
  const isPaymentVisible = view === "payment";
  const isEditRequestVisible = view === "edit-request";
  const isParticipantsVisible = view === "participants";
  
  // Main list should be hidden (but mounted) when any overlay is showing
  const isMainListHidden = !isListVisible;
  
  // ========================================
  // EVENT HANDLERS
  // ========================================
  const handleJoinWorkshop = async (workshopId, isPaid = false) => {
    try {
      if (isPaid) {
        navigate(`/workshops/${workshopId}/payment`);
        return { success: true, redirected: true };
      } else {
        const response = await workshopApi.applyToWorkshop(workshopId);
        
        if (response.success) {
          const status = response.status || response.data?.status || response.participant?.status;
          const message = response.message?.toLowerCase() || "";
          const isInstantJoin = 
            status === "approved" || 
            status === "joined" ||
            message.includes("approved") ||
            message.includes("joined") ||
            message.includes("enrolled") ||
            response.autoApproved === true ||
            response.data?.autoApproved === true;
          
          return { 
            success: true, 
            isInstantJoin,
            status: isInstantJoin ? "approved" : "pending",
            message: isInstantJoin 
              ? "🎉 Successfully joined! You're now enrolled in this workshop."
              : "✅ Application submitted! Please wait for organizer approval."
          };
        }
        return { success: false };
      }
    } catch (error) {
throw error;
    }
  };

  const handleCreateWorkshop = (createdWorkshop) => {
navigate(`/workshops/${createdWorkshop._id}`);
  };

  return (
    <div className="persistent-workshops-layout">
      {/* 
        CRITICAL: All container divs are ALWAYS rendered in FIXED ORDER.
        This prevents React sibling reconciliation from remounting.
        Content inside is conditionally rendered, but containers are stable.
      */}
      
      {/* Layer 1: Main Workshops List - ALWAYS MOUNTED */}
      <div
        ref={listContainerRef}
        className={`persistent-workshops-layout__list ${
          isMainListHidden ? "persistent-workshops-layout__list--hidden" : ""
        }`}
        aria-hidden={isMainListHidden}
      >
        <WorkshopsPage />
      </div>
      
      {/* Layer 2: My Workshops - container always rendered */}
      <div
        className={`persistent-workshops-layout__overlay ${
          !isMyWorkshopsVisible ? "persistent-workshops-layout__overlay--hidden" : ""
        }`}
        aria-hidden={!isMyWorkshopsVisible}
      >
        {isMyWorkshopsVisible && <MyWorkshops currentUser={user} />}
      </div>
      
      {/* Layer 3: Workshop Detail - container always rendered */}
      <div
        className={`persistent-workshops-layout__overlay ${
          !isDetailVisible ? "persistent-workshops-layout__overlay--hidden" : ""
        }`}
        aria-hidden={!isDetailVisible}
      >
        {isDetailVisible && workshopId && (
          <WorkshopDetail 
            key={workshopId}
            workshopId={workshopId}
            onJoinWorkshop={handleJoinWorkshop} 
          />
        )}
      </div>
      
      {/* Layer 4: Create Workshop Form - container always rendered */}
      <div
        className={`persistent-workshops-layout__overlay ${
          !isCreateVisible ? "persistent-workshops-layout__overlay--hidden" : ""
        }`}
        aria-hidden={!isCreateVisible}
      >
        {isCreateVisible && (
          <WorkshopCreationForm
            onSubmit={handleCreateWorkshop}
            onCancel={() => navigate("/workshops")}
          />
        )}
      </div>
      
      {/* Layer 5: Edit Workshop Form - container always rendered */}
      <div
        className={`persistent-workshops-layout__overlay ${
          !isEditVisible ? "persistent-workshops-layout__overlay--hidden" : ""
        }`}
        aria-hidden={!isEditVisible}
      >
        {isEditVisible && workshopId && (
          <WorkshopCreationForm
            key={`edit-${workshopId}`}
            workshopId={workshopId}
            isEditMode={true}
            onSubmit={(workshop) => navigate(`/workshops/${workshop._id}`)}
            onCancel={() => navigate("/workshops/my-workshops")}
          />
        )}
      </div>
      
      {/* Layer 6: Payment Page - container always rendered */}
      <div
        className={`persistent-workshops-layout__overlay ${
          !isPaymentVisible ? "persistent-workshops-layout__overlay--hidden" : ""
        }`}
        aria-hidden={!isPaymentVisible}
      >
        {isPaymentVisible && workshopId && (
          <PaymentProofUpload key={`payment-${workshopId}`} workshopId={workshopId} />
        )}
      </div>
      
      {/* Layer 7: Edit Request - container always rendered */}
      <div
        className={`persistent-workshops-layout__overlay ${
          !isEditRequestVisible ? "persistent-workshops-layout__overlay--hidden" : ""
        }`}
        aria-hidden={!isEditRequestVisible}
      >
        {isEditRequestVisible && workshopId && (
          <EditRequest key={`edit-request-${workshopId}`} workshopId={workshopId} />
        )}
      </div>
      
      {/* Layer 8: Participants Management - container always rendered */}
      <div
        className={`persistent-workshops-layout__overlay ${
          !isParticipantsVisible ? "persistent-workshops-layout__overlay--hidden" : ""
        }`}
        aria-hidden={!isParticipantsVisible}
      >
        {isParticipantsVisible && workshopId && (
          <ParticipantManagement key={`participants-${workshopId}`} workshopId={workshopId} />
        )}
      </div>
    </div>
  );
};

export default PersistentWorkshopsLayout;
